import { contractApi } from '@api/queries/contract';
import {
  type ContractData,
  type ContractDataType,
  type GetContractsResponseType,
  type NewContractResponseType,
  type OpenChestResponseType,
  type PaidSpecContractType,
  type ProductOfDayDataType,
  type ResponseContractDataType,
  type SpecContractType,
} from '@common/types/contract';
import { createSlice, isAnyOf, type PayloadAction } from '@reduxjs/toolkit';

import {
  nextSpecContractTimeFields,
  specContractList,
} from '@/common/constants/contract';
import { addExpiredAtField } from '@/common/lib/contract/add-expired-at-field';
import { getEndTimeFromNow } from '@/common/lib/date';
import { transfromContractResponseToState } from '@/common/lib/transformContractResposeToState';
import { type GoodsType } from '@/common/types/goods';
import { type PlantTypeText } from '@/common/types/plant';
import {
  type ActivateVipStatusBuyLinkResponseType,
  type ActivateVipStatusResponseType,
} from '@/common/types/query/user';
import { type ResourceTypeText } from '@/common/types/resource';

interface ContractInitialState {
  list: ContractDataType[] | null;
  nextGoldContractTime?: number | null;
  nextGrandContractTime?: number | null;
  nextCustomContractTime?: number | null;
  nextBlitzContractTime?: number | null;
  userPoints: number | null;
  totalPointsEarn: number | null;
  chestBoxRequiredPoints: number | null;
  availableChestsCount: number | null;
  chest: OpenChestResponseType | null;
  nextSpecContractEndTime: Record<
    SpecContractType | PaidSpecContractType,
    string | null
  >;
  productOfDay: ProductOfDayDataType[] | null;
  buyPlusCountract: {
    isOpen: boolean;
    id: string | null;
  };
}

const initialState: ContractInitialState = {
  list: null,
  nextGoldContractTime: null,
  nextGrandContractTime: null,
  nextCustomContractTime: null,
  userPoints: null,
  totalPointsEarn: null,
  chestBoxRequiredPoints: null,
  availableChestsCount: null,
  chest: null,
  nextSpecContractEndTime: {
    Custom: null,
    Golden: null,
    GoldenPlus: null,
    Grand: null,
    GrandPlus: null,
    Blitz: null,
    BlitzPlus: null,
  },
  productOfDay: null,
  buyPlusCountract: {
    isOpen: false,
    id: null,
  },
};

const contractSlice = createSlice({
  name: 'contarct',
  initialState,
  reducers: {
    setContractList: (
      state,
      { payload }: PayloadAction<ResponseContractDataType[]>,
    ) => {
      state.list = payload.map(transfromContractResponseToState);
    },
    removeContractById: (state, { payload }: PayloadAction<string>) => {
      if (state.list) {
        const index = state.list.findIndex(({ id }) => id === payload);

        if (index >= 0) {
          state.list.splice(index, 1);
        }
      }
    },
    fixResourceInCustomContract: (
      state,
      { payload }: PayloadAction<ResourceTypeText | PlantTypeText | GoodsType>,
    ) => {
      if (state.list) {
        const customInd = state.list.findIndex(({ type }) => type === 'Custom');

        if (customInd >= 0) {
          const contractData = state.list[customInd].data as ContractData;

          // Перевіряємо, чи існує payload у contractData
          if (!contractData[payload]) {
            console.warn(`Resource ${payload} not found in contract data`);

            return;
          }

          // Якщо payload уже зафіксований, скидаємо фіксацію
          if (contractData[payload].fixed) {
            contractData[payload].fixed = false;

            return;
          }

          // Скидаємо fixed для всіх елементів
          Object.keys(contractData).forEach((key) => {
            if (
              contractData[key as ResourceTypeText | PlantTypeText | GoodsType]
                .fixed
            ) {
              contractData[
                key as ResourceTypeText | PlantTypeText | GoodsType
              ].fixed = false;
            }
          });

          // Фіксуємо новий елемент
          contractData[payload].fixed = true;
        }
      }
    },
    setBuyContractPlus: (
      state,
      { payload }: PayloadAction<{ isOpen: boolean; id?: string }>,
    ) => {
      const newId = payload.id || null;
      state.buyPlusCountract = { isOpen: payload.isOpen, id: newId };
    },
    updateContract: (state, { payload }: PayloadAction<ContractDataType>) => {
      if (!state.list) {
        return;
      }
      const contract = state.list.find(({ id }) => id === payload.id);

      if (contract) {
        Object.assign(contract, payload);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        isAnyOf(
          contractApi.endpoints.getContracts.matchFulfilled,
          contractApi.endpoints.rejectContract.matchFulfilled,
          contractApi.endpoints.openContract.matchFulfilled,
        ),
        (
          state,
          {
            payload,
          }: PayloadAction<
            | GetContractsResponseType
            | void
            | ActivateVipStatusResponseType
            | ActivateVipStatusBuyLinkResponseType
          >,
        ) => {
          if (!payload || 'link' in payload || 'address' in payload) {
            return;
          }

          nextSpecContractTimeFields.forEach((field, index) => {
            state[field] = payload[field] || null;

            if (payload[field]) {
              state.nextSpecContractEndTime[specContractList[index]] =
                getEndTimeFromNow(payload[field]);
            }
          });
          state.list = payload.contracts.map(transfromContractResponseToState);
          state.userPoints = payload.userPoints;
          state.totalPointsEarn = payload.totalPointsEarn;
          state.chestBoxRequiredPoints = payload.chestBoxRequiredPoints;
          state.availableChestsCount = payload.availableChestsCount;

          if (payload.productOfDay) {
            state.productOfDay = payload.productOfDay.map(addExpiredAtField);
          }
        },
      )
      .addMatcher(
        contractApi.endpoints.shufleCustomContract.matchFulfilled,

        (state, { payload }) => {
          const contractInd = state.list?.findIndex(
            (el) => el.type === 'Custom',
          );

          if (
            state.list &&
            typeof contractInd === 'number' &&
            contractInd >= 0
          ) {
            state.list[contractInd] = payload;
          }
        },
      )
      .addMatcher(
        isAnyOf(contractApi.endpoints.completeContract.matchFulfilled),
        (state, { payload }: PayloadAction<GetContractsResponseType>) => {
          nextSpecContractTimeFields.forEach((field, index) => {
            state[field] = payload[field] || null;

            if (payload[field]) {
              state.nextSpecContractEndTime[specContractList[index]] =
                getEndTimeFromNow(payload[field]);
            }
          });
          state.userPoints = payload.userPoints;
          state.totalPointsEarn = payload.totalPointsEarn;
          state.chestBoxRequiredPoints = payload.chestBoxRequiredPoints;
          state.availableChestsCount = payload.availableChestsCount;
          state.productOfDay = payload.productOfDay.map(addExpiredAtField);
        },
      )
      .addMatcher(
        contractApi.endpoints.openChest.matchFulfilled,
        (state, { payload }: PayloadAction<OpenChestResponseType | null>) => {
          state.chest = payload;
        },
      )
      .addMatcher(contractApi.endpoints.applyChest.matchFulfilled, (state) => {
        if (state.availableChestsCount) {
          state.availableChestsCount -= 1;
        }
      })
      .addMatcher(
        contractApi.endpoints.newContract.matchFulfilled,
        (state, { payload }: PayloadAction<NewContractResponseType>) => {
          state.list = payload.contracts;
          state.userPoints = payload.userPoints;
          state.totalPointsEarn = payload.totalPointsEarn;
          state.chestBoxRequiredPoints = payload.chestBoxRequiredPoints;
          state.availableChestsCount = payload.availableChestsCount;
        },
      )
      .addMatcher(
        contractApi.endpoints.openProductOfDayChestbox.matchFulfilled,
        (state, { payload, meta }) => {
          if (!state.productOfDay) return;
          const index = state.productOfDay?.findIndex(
            (product) => product.id === meta.arg.originalArgs.id,
          );

          if (index === -1) return;
          state.productOfDay[index as number].chestboxReward = payload;
          state.productOfDay[index as number].chestBoxOpenedAt =
            new Date().toISOString();
        },
      )
      .addMatcher(
        contractApi.endpoints.buyExtendTime.matchFulfilled,
        (state, { payload }: PayloadAction<ResponseContractDataType>) => {
          const targetContract = state.list?.find(
            ({ id }) => id === payload.id,
          );

          if (targetContract) {
            Object.assign(
              targetContract,
              transfromContractResponseToState(payload),
            );
          }
        },
      );
  },
});

export const {
  actions: {
    setContractList,
    removeContractById,
    fixResourceInCustomContract,
    setBuyContractPlus,
    updateContract,
  },
} = contractSlice;
export default contractSlice.reducer;
